import "./Parts.scss";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect } from "react";
import BlueButton from "../../components/BlueButton/BlueButton";
import AddGif from "./../../assets/add-gif.gif";
import SearchIcon from "@mui/icons-material/Search";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import { DataGrid } from "@mui/x-data-grid";
import { PartsColumns } from "../../SourceData";
import api from "../../utils/api";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import MessageModals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import { usePermissions } from "./../../hooks/usePermissions";
import { toast } from "react-toastify";

const Parts = () => {
  const { hasPermission } = usePermissions();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  // State variables
  const [query, setQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(10); // Default value: "الجميع"

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Swipeable modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalView, setModalView] = useState("create");
  const [editServiceData, setEditServiceData] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Services data state
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Only update searchTerm if query is empty or has more than 2 characters
    if (value === "" || value.length > 2) {
      setSearchTerm(value);
      // Reset to first page when filters change
      setPage(1);
    }
  };

  // Handle category change
  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setPage(1); // Reset to first page when filters change
  };

  // Fetch services data
  const fetchServices = async () => {
    if (!hasPermission(120)) {
      return;
    }
    setLoading(true);
    try {
      let params = {
        page,
        limit: pageSize,
      };

      // Add search term if it's not empty
      if (searchTerm) {
        params.name = searchTerm;
        params.local = "ar";
      }

      // Add category filter if not "الجميع" (All)
      if (selectedCategory !== 10) {
        params.category = selectedCategory;
      }

      const response = await api.get("/utility-items", { params });

      if (response.data.status === "success") {
        setServices(response.data.data);
        setTotalCount(response.data.totalCount);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare service data for editing
  const handleEditService = (serviceRow) => {
    // Check edit permission
    if (!hasPermission(122)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    // Format the data for the SwipeableModals component
    setEditServiceData(serviceRow);
    setModalView("edit");
    setModalOpen(true);
  };

  // Fetch categories for the dropdown
  const fetchCategories = async () => {
    if (!hasPermission(120)) {
      return;
    }
    try {
      const response = await api.get("/categories", {
        params: {
          limit: 100, // Get a large number to show all categories in dropdown
        },
      });

      if (response.data.status === "success") {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Effect to fetch services when dependencies change
  useEffect(() => {
    fetchServices();
  }, [page, pageSize, searchTerm, selectedCategory]);

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Toggle service active state
  const handleActive = async (id) => {
    // Check activate permission
    if (!hasPermission(124)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setLoading(true);
    try {
      await api.patch(`/utility-items/${id}/toggle-status`);
      toast.success("تم تغير الحالة بنجاح");
      fetchServices();
    } catch (error) {
      console.error("Error toggling service:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening create service modal
  const handleOpenCreateModal = () => {
    // Check create permission
    if (!hasPermission(119)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setModalView("create");
    setEditServiceData(null);
    setModalOpen(true);
  };

  // Handle delete service
  const handleDeleteService = (serviceRow) => {
    // Check delete permission
    if (!hasPermission(123)) {
      toast.error("ليس لديك هذه الصلاحية");
      return;
    }

    setSelectedService(serviceRow);
    setOpenDelete(true);
  };

  // Handle modal close and refresh data if needed
  const handleModalClose = (dataChanged = false) => {
    setModalOpen(false);
    if (dataChanged) {
      fetchServices();
    }
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        // Only show switch if user has activate permission
        if (!hasPermission(124)) {
          return <span>{isActive ? "نشط" : "غير نشط"}</span>;
        }

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 180,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const canEdit = hasPermission(122);
        const canDelete = hasPermission(123);

        // If user has no edit or delete permissions, return empty cell
        if (!canEdit && !canDelete) {
          return <div className="cell-action"></div>;
        }

        return (
          <div className="cell-action">
            {canEdit && (
              <div
                className="cell-btn"
                onClick={() => handleEditService(params.row)}
              >
                <img src={EditUser} alt="تعديل" />
              </div>
            )}

            {canDelete && (
              <div
                className="cell-btn"
                onClick={() => handleDeleteService(params.row)}
              >
                <img src={DeleteUser} alt="حذف" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="products">
      <div className="special-header">
        <p className="title">
          قطع الغيار <span className="count-badge">{totalCount || 0}</span>
        </p>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder="ابحث باسم الخدمة...."
              onChange={handleSearchChange}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              sx={{
                width: isMobile ? "100%" : "180px",
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                shrink
                sx={{
                  background: "white",
                  transformOrigin: "right top",
                  transform: "translate(14px, -9px) scale(0.75)",
                  right: 24,
                  left: "auto",
                  paddingLeft: "4px",
                  paddingRight: "4px",
                }}
              >
                الاقسام
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                displayEmpty={true}
                sx={{ borderRadius: "8px", height: "48px" }}
              >
                <MenuItem value={10}>الجميع</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>
                    {category?.name?.ar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Only show add button if user has create permission */}

          <BlueButton
            text="اضافة قطعة جديدة"
            size={isMobile ? "full" : isTablet ? "full" : "large"}
            variant="fill"
            onClick={handleOpenCreateModal}
            icon={<img src={AddGif} alt="" />}
          />
        </div>
      </div>
      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={PartsColumns.concat(actionColumn)}
          rows={services.map((service, index) => ({
            ...service,
            // Generate unique id if not present
            id: service._id || index,
            // Add index for numbering
            index: (page - 1) * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalCount}
          initialState={{
            pagination: {
              paginationModel: { pageSize, page: page - 1 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          onPaginationModelChange={(model) => {
            // Handle page change
            if (model.page !== page - 1) {
              setPage(model.page + 1);
            }

            // Handle page size change
            if (model.pageSize !== pageSize) {
              setPageSize(model.pageSize);
              setPage(1); // Reset to first page when changing page size
            }
          }}
          getRowClassName={(params) => "data-grid-row"}
          localeText={{
            MuiTablePagination: {
              labelRowsPerPage: "خدمة في الصفحة",
            },
          }}
        />

        {/* SwipeableModals for creating/editing services - only render if user has permissions */}
        {(hasPermission(119) || hasPermission(122)) && (
          <SwipeableModals
            open={modalOpen}
            onClose={() => handleModalClose()}
            onOpen={() => setModalOpen(true)}
            currentView={modalView}
            editData={editServiceData}
            onSubmitSuccess={() => handleModalClose(true)}
            categories={categories}
          />
        )}

        {/* Delete Confirmation Modal - only render if user has delete permission */}
        {hasPermission(123) && (
          <MessageModals
            open={openDelete}
            currentView="delete"
            deleteMessage={"تم حذف القطعه بنجاح"}
            route={selectedService ? `/utility-items/${selectedService._id}` : ""}
            onDataChange={fetchServices}
            onClose={() => {
              setOpenDelete(false);
              setSelectedService(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Parts;
